/**
 * Component: ProductFormModal
 * Modal para criar/editar produto
 */
import React, { useState, useEffect } from 'react';
import { ProductDTO } from '../../services/modules/productService';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import { Button } from '../ui/Button';
import { useCurrencyInput } from '../../hooks';

interface ProductFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (productData: { name: string; value: number; description: string }) => Promise<void>;
    product?: ProductDTO | null;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
    isOpen,
    onClose,
    onSave,
    product
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        displayValue: value,
        rawValue: numericValue,
        setValue: setCurrencyValue,
        handleChange: handleValueChange
    } = useCurrencyInput(0);

    useEffect(() => {
        if (isOpen) {
            if (product) {
                setName(product.name);
                setCurrencyValue(product.value);
                setDescription(product.description);
            } else {
                setName('');
                setCurrencyValue(0);
                setDescription('');
            }
        }
    }, [isOpen, product, setCurrencyValue]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || numericValue <= 0) return;

        try {
            setIsSubmitting(true);
            await onSave({
                name: name.trim(),
                value: numericValue,
                description: description.trim()
            });
        } catch (error) {
            console.error('Error saving product:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <Modal.Header onClose={onClose}>
                {product ? 'Editar Produto' : 'Novo Produto'}
            </Modal.Header>
            <Modal.Body>
                <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label="Nome do Produto *"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Produto Premium"
                        required
                    />

                    <Input
                        id="product-value"
                        label="Valor (R$) *"
                        type="text"
                        value={value}
                        onChange={handleValueChange}
                        icon={<span className="text-sm">R$</span>}
                        placeholder="0,00"
                        required
                    />

                    <Textarea
                        label={`Descrição (${description.length}/100)`}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={100}
                        minRows={3}
                        className="resize-none"
                        placeholder="Descrição do produto (opcional)"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button type="submit" form="product-form" disabled={isSubmitting}>
                    {isSubmitting ? 'Salvando...' : (product ? 'Salvar' : 'Criar')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductFormModal;
